<template>
   <div>
    <desktop-blocked-view
      v-if="isDesktopBlocked"
      :logo-url="resolvedCompanyLogo"
    />

    <mock-up-main v-else>
    <div
      class="attendance-preview-wrapper position-relative"
      :style="previewContainerStyles"
    >
      <universal-qr-modal
        v-model="showQrDialog"
        :title="value.visitName || 'کیوآر حضور و غیاب'"
        :subtitle="'برای دریافت اطلاعات، کیوآر زیر را اسکن کنید.'"
        :identifier="value?.identifier || ''"
        :profile-avatar="value?.photo?.profile || ''"
        :primary-color="primaryActionColor"
        :svg-data="qrSvgDataUrl"
      />

      <v-btn
        fab
        x-small
        elevation="4"
        class="qr-floating-btn"
        title="مشاهده و اسکن کیوآر کد"
        @click="showQrDialog = true"
      >
        <v-icon size="18">mdi-qrcode-scan</v-icon>
      </v-btn>

      <div class="company-header elevation-2 text-center" :style="{ background: headerBackground }">
        <div class="header-overlay"></div>

        <div class="header-content">
          <v-avatar size="66" class="elevation-3 mb-2 bg-white company-avatar">
            <v-img
              v-if="resolvedCompanyLogo"
              :src="resolvedCompanyLogo"
              contain
              class="rounded-circle"
            />
            <v-icon v-else size="38" :style="{ color: primaryThemeColor }">mdi-domain</v-icon>
          </v-avatar>

          <h4
            class="font-weight-bold mb-1 text-truncate px-3 company-title"
            :style="{ color: headerTextColor }"
          >
            {{ companyInfo.organizationName || 'نام مجموعه / سازمان' }}
          </h4>
          <span
            v-if="companyInfo.subOrgan"
            class="text-caption text-truncate px-3 font-weight-light d-block company-sub"
            :style="{ color: headerTextColor, opacity: 0.85 }"
          >
            {{ companyInfo.subOrgan }}
          </span>

          <div class="d-flex justify-center mt-3">
            <v-btn-toggle
              v-model="previewMode"
              mandatory
              dense
              rounded
              class="preview-toggle elevation-1"
            >
              <v-btn small value="form" :style="previewMode === 'form' ? activeToggleStyle : deactiveToggleStyle">
                <v-icon x-small left>mdi-form-select</v-icon>
                فرم پرسنل
              </v-btn>
              <v-btn small value="action" :style="previewMode === 'action' ? activeToggleStyle : deactiveToggleStyle">
                <v-icon x-small left>mdi-clock-check-outline</v-icon>
                ثبت تردد
              </v-btn>
            </v-btn-toggle>
          </div>
        </div>
      </div>

      <div class="preview-body px-3 py-3" :style="{ color: bodyTextColor }">
        <transition name="fade-transition" mode="out-in">
          <!-- تب اول: فرم ثبت اطلاعات پرسنل -->
          <div v-if="previewMode === 'form'" key="form-view">
            <draggable
              v-model="draggableFields"
              handle=".drag-handle"
              ghost-class="ghost-field"
              animation="250"
            >
              <div
                v-for="(item, index) in draggableFields"
                :key="item.id || index"
                class="field-card mb-2 pa-2 rounded-lg elevation-1 d-flex align-center"
              >
                <v-icon class="drag-handle mr-1 ml-1" color="grey darken-1" style="cursor: grab;">
                  mdi-drag-vertical
                </v-icon>

                <div class="flex-grow-1 pr-1">
                  <div class="d-flex justify-space-between align-center mb-1">
                    <label class="text-caption font-weight-bold text-truncate" :style="{ color: bodyTextColor }">
                      {{ item.title }}
                      <span v-if="item.required" class="error--text font-weight-black">*</span>
                    </label>
                  </div>

                  <custom-date-picker
                    v-if="item.type === 'date' || item.type === 'datetime'"
                    v-model="previewFormModels[item.id]"
                    :label="item.title"
                    auto-submit
                    display-format="jYYYY-jMM-jDD HH:mm:ss"
                    format="YYYY-MM-DD HH:mm:ss"
                    :type="item.type === 'datetime' ? 'datetime' : 'date'"
                    class="field-input custom-input"
                  />

                  <timepicker
                    v-else-if="item.type === 'time'"
                    v-model="previewFormModels[item.id]"
                    :label="item.title"
                    class="field-input custom-input"
                  />

                  <v-select
                    v-else-if="item.type === 'select'"
                    v-model="previewFormModels[item.id]"
                    :items="item.options && item.options.length ? item.options : ['انتخاب دپارتمان / بخش']"
                    outlined
                    dense
                    hide-details
                    :placeholder="item.placeholder || 'یک مورد را انتخاب کنید'"
                    class="field-input custom-input"
                    append-icon="mdi-chevron-down"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon small class="me-1" :style="{ color: primaryThemeColor }">
                        {{ item.icon }}
                      </v-icon>
                    </template>
                  </v-select>

                  <v-text-field
                    v-else
                    v-model="previewFormModels[item.id]"
                    outlined
                    dense
                    hide-details
                    :placeholder="item.placeholder"
                    class="field-input custom-input"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon small class="me-1" :style="{ color: primaryThemeColor }">
                        {{ item.icon }}
                      </v-icon>
                    </template>
                  </v-text-field>
                </div>
              </div>
            </draggable>

            <v-btn
              block
              dark
              class="mt-4 rounded-pill main-action-btn font-weight-bold elevation-2"
              :style="{ background: headerBackground, color: '#ffffff' }"
              @click="testPopup('success', 'ثبت فرم')"
            >
              <v-icon left small>mdi-check-circle-outline</v-icon>
              ثبت و ارسال اطلاعات پرسنل
            </v-btn>
          </div>

          <!-- تب دوم: ایستگاه ثبت تردد -->
          <div v-else key="action-view">
            <v-card class="rounded-xl pa-3 mb-3 bg-white elevation-1 border-light">
              <div class="d-flex justify-space-between align-center mb-2">
                <div class="d-flex align-center">
                  <v-icon :color="isWithinRadius ? 'success' : 'error'" size="18" class="mr-1">
                    {{ isWithinRadius ? 'mdi-map-marker-radius' : 'mdi-map-marker-off' }}
                  </v-icon>
                  <span 
                    class="text-caption font-weight-bold"
                    :class="isWithinRadius ? 'success--text' : 'error--text'"
                  >
                    {{ isWithinRadius ? 'موقعیت: داخل محدوده مجاز' : 'موقعیت: خارج از محدوده مجاز' }}
                  </span>
                </div>
                <v-chip x-small :color="isWithinRadius ? 'success' : 'error'" outlined>
                  {{ companyInfo.allowedRadius || 100 }} متر مجاز
                </v-chip>
              </div>

              <!-- هشدار موقعیت (قفل نمی‌کند فقط اخطار می‌دهد) -->
              <v-alert
                v-if="!isWithinRadius"
                dense
                outlined
                type="warning"
                class="rounded-lg text-caption my-2 text-right"
              >
                فاصله شما ({{ currentDistance }} متر) بیشتر از حد مجاز است. تردد شما <b>با خطای مکانی</b> ثبت خواهد شد.
              </v-alert>

              <div v-if="hasQrFromUrl" class="text-caption grey--text text--darken-2 mb-2 d-flex align-center">
                <v-icon x-small color="success" class="ml-1">mdi-check-decagram</v-icon>
                کیوآر حضور و غیاب از لینک شناسایی شد.
              </div>

              <div class="my-2">
                <div class="my-2 pa-2 rounded-lg d-flex align-center justify-space-between bg-light border-subtle">
                  <div class="d-flex align-center">
                    <v-icon small color="primary" class="ml-1">mdi-calendar-clock</v-icon>
                    <span class="text-caption font-weight-bold grey--text text--darken-2">زمان تردد:</span>
                  </div>
                  <p class="mb-0 text-caption font-weight-bold dir-ltr attendance-time-badge">
                    {{ nowTimeString }}
                  </p>
                </div>
              </div>

              <v-divider class="my-2"></v-divider>

              <div class="shifts-container">
                <div class="d-flex align-center justify-space-between mb-2">
                  <span class="text-caption font-weight-bold grey--text text--darken-2">
                    <v-icon x-small color="primary" class="ml-1">mdi-clock-outline</v-icon>
                    شیفت‌های کاری مجموعه:
                  </span>
                  <span class="text-caption grey--text">
                    {{ activeShiftsList.length > 0 ? `${activeShiftsList.length} شیفت فعال` : 'تردد شناور' }}
                  </span>
                </div>

                <div
                  v-if="activeShiftsList.length === 0"
                  class="pa-3 text-center rounded-lg bg-light border-subtle"
                >
                  <v-icon small color="grey darken-1" class="mb-1">mdi-clock-time-four-outline</v-icon>
                  <p class="text-caption grey--text text--darken-2 mb-0">
                    شیفت کاری خاصی تعریف نشده است (تردد پرسنل به‌صورت شناور ثبت می‌شود).
                  </p>
                </div>

                <div
                  v-else
                  v-for="(shift, sIdx) in activeShiftsList"
                  :key="'preview-shift-' + sIdx"
                  class="shift-row pa-2 mb-1 rounded-lg d-flex align-center justify-space-between"
                  :class="{ 'shift-row-selected': selectedShiftIndex === sIdx }"
                  @click="selectedShiftIndex = sIdx"
                >
                  <div class="d-flex align-center">
                    <v-icon
                      small
                      :color="selectedShiftIndex === sIdx ? 'primary' : 'grey'"
                      class="ml-1"
                    >
                      {{ selectedShiftIndex === sIdx ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}
                    </v-icon>
                    <span class="text-caption font-weight-medium">
                      {{ shift.name || ('شیفت ' + (sIdx + 1)) }}
                    </span>
                  </div>

                  <div class="text-caption font-weight-bold dir-ltr" :style="{ color: primaryThemeColor }">
                    {{ shift.startTime || '08:00' }} - {{ shift.endTime || '17:00' }}
                  </div>
                </div>
              </div>
            </v-card>

            <!-- دکمه‌های پویا از تنظیمات -->
            <div class="d-flex flex-column gap-2">
              <v-btn
                v-for="(btn, idx) in mappedActionButtons"
                :key="btn.id"
                large
                block
                :dark="!btn.outlined"
                :outlined="btn.outlined"
                :color="btn.outlined ? btn.color : undefined"
                :loading="isSubmitting"
                class="rounded-xl elevation-2 font-weight-bold mb-2 py-6 main-action-btn"
                :style="!btn.outlined ? { background: btn.color || headerBackground, color: '#ffffff' } : { background: '#ffffff' }"
                @click="handleAttendanceAction(btn.label, btn.id)"
              >
                <v-icon left>{{ btn.icon }}</v-icon>
                {{ btn.label }}
              </v-btn>

              <div v-if="mappedActionButtons.length === 0" class="text-center pa-4 grey--text text-caption">
                هیچ دکمه‌ای برای تردد فعال نشده است.
              </div>
            </div>
          </div>
        </transition>

        <!-- پنل تست -->
        <div class="test-actions-bar mt-4 pt-2">
          <div class="text-caption grey--text text-center mb-1 font-weight-bold">شبیه‌سازی خطاهای احتمالی</div>
          <div class="d-flex justify-space-around">
            <v-btn x-small text color="error" class="rounded-pill" @click="testPopup('error')">
              <v-icon x-small left>mdi-alert-circle</v-icon>
              خطای پایگاه داده
            </v-btn>
            <v-btn x-small text color="warning" class="rounded-pill" @click="testPopup('warning')">
              <v-icon x-small left>mdi-account-question</v-icon>
              تست پرسنل ناشناس
            </v-btn>
          </div>
        </div>
      </div>

      <!-- پاپ‌آپ پیش‌فرض سیستم برای تاییدیه تردد -->
      <attendance-feedback-popup
        :show="popup.show"
        :type="popup.type"
        :title="popup.title"
        :message="popup.message"
        :time="popup.time"
        @close="popup.show = false"
      />

      <!-- دیالوگ اختصاصی برای پرسنل ناشناس -->
      <v-dialog v-model="unidentifiedDialog" max-width="400" persistent>
        <v-card class="rounded-xl text-center pa-5 bg-white">
          <v-icon color="warning" size="72" class="mb-4">mdi-account-question</v-icon>
          <h3 class="font-weight-bold mb-2">شناسایی ناموفق</h3>
          <p class="text-caption grey--text text--darken-1 mb-5">
            شماره پرسنلی یا اطلاعات شما در سامانه یافت نشد. جهت ثبت تردد، ابتدا باید در سیستم ثبت‌نام کنید.
          </p>
          <div class="d-flex flex-column gap-2 w-100">
            <v-btn large color="primary" block class="rounded-lg font-weight-bold elevation-1" @click="goToRegistration">
              <v-icon left>mdi-form-select</v-icon>
              بله، تکمیل فرم ثبت‌نام
            </v-btn>
            <v-btn large outlined color="grey darken-2" block class="rounded-lg font-weight-bold mt-2" @click="unidentifiedDialog = false">
              بستن و انصراف
            </v-btn>
          </div>
        </v-card>
      </v-dialog>

      <!-- اسکنر مدرن دوربین -->
      <transition name="scanner-fade">
        <div v-if="scannerDialog" class="in-mockup-scanner-overlay ma-1 mt-20">
          <div class="scanner-top-bar d-flex align-center justify-space-between px-3 py-2">
            <span class="white--text font-weight-bold pa-3">
              اسکن بارکد پایانه جهت {{ pendingAction }}
            </span>
            <v-btn icon small dark class="close-scanner-btn" @click="closeScanner">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>

          <div class="scanner-viewfinder-container">
            <video ref="cameraVideo" class="scanner-video-element" playsinline></video>
            <div class="scanner-target-box">
              <div class="corner-border top-left"></div>
              <div class="corner-border top-right"></div>
              <div class="corner-border bottom-left"></div>
              <div class="corner-border bottom-right"></div>
              <div class="laser-beam"></div>
            </div>
          </div>

          <div class="scanner-bottom-bar text-center py-2 px-3">
            <span class="text-caption white--text opacity-80">
              کیوآر کد را درون کادر قرار دهید
            </span>
          </div>
        </div>
      </transition>
    </div>
  </mock-up-main>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import AttendanceFeedbackPopup from "./AttendanceFeedbackPopup.vue";
import MockUpMain from "@/view/pages/businessPage/components/mockUp.vue";
import Timepicker from "../../../../components/Timepicker.vue";
import CustomDatePicker from "../../../../content/custom-date-picker.vue";
import UniversalQrModal from "../../../../components/UniversalQrModal.vue";
import QrScanner from "qr-scanner";
import { submitAttendanceRecord } from "./attendanceAuthService.js";
import DesktopBlockedView from "./DesktopBlockedView.vue";

export default {
  name: "AttendanceQrView",

  components: {
    draggable,
    AttendanceFeedbackPopup,
    MockUpMain,
    Timepicker,
    CustomDatePicker,
    UniversalQrModal,
    DesktopBlockedView,
  },

  props: {
    value: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    qrSvgDataUrl: {
      type: String,
      default: "",
    },
    isAdminPreview: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      isDesktopBlocked: false,
      showQrDialog: false,
      previewMode: "action",
      selectedShiftIndex: 0,
      attendanceDateTime: this.nowDateTimeString(),
      nowTimeString: "",
      previewFormModels: {},

      isWithinRadius: true,
      currentDistance: 0,
      userLocation: null,

      urlQrCode: null,
      scannerDialog: false,
      pendingAction: "ورود",
      pendingActionId: "check_in",
      qrScannerInstance: null,

      isSubmitting: false,
      selectedAuthMethod: "passkey",
      vapidPublicKey: process.env.VUE_APP_VAPID_PUBLIC_KEY || "",

      unidentifiedDialog: false,

      popup: {
        show: false,
        type: "success",
        title: "",
        message: "",
        time: "",
      },

      fallbackDefaults: [
        { id: 3001, title: "شماره پرسنلی", action: "mdi-badge-account-horizontal", items: [{ type: "number", action: true, optional: true }] },
        { id: 3002, title: "نام", action: "mdi-account", items: [{ type: "text", action: true, optional: true }] },
        { id: 3003, title: "نام خانوادگی", action: "mdi-account-tie", items: [{ type: "text", action: true, optional: true }] },
        { id: 3004, title: "شماره موبایل", action: "mdi-cellphone", items: [{ type: "text", action: true, optional: true }] },
        { id: 3005, title: "کد ملی", action: "mdi-card-account-details-outline", items: [{ type: "text", action: true, optional: true }] },
        { id: 3006, title: "واحد / دپارتمان", action: "mdi-domain", items: [{ type: "select", action: true, optional: false, selectItems: ["اداری", "فنی", "پشتیبانی"] }] },
        { id: 3007, title: "سمت شغلی", action: "mdi-briefcase-outline", items: [{ type: "text", action: true, optional: false }] },
      ],
    };
  },

  computed: {
    hasQrFromUrl() {
      return !!this.urlQrCode;
    },

    primaryActionColor() {
      return this.value?.pallete?.bodyColor || "#1976D2";
    },

    companyInfo() {
      return (
        this.value?.CompanyAttendanceBaseInfo ||
        this.value?.baseInfo ||
        {}
      );
    },

    mappedActionButtons() {
    const settings = 
        this.value?.AttendanceActionSettings?.actions ||
        this.value?.AttendanceActionSettings ||
        this.value?.actionSettings?.actions ||
        this.value?.actionSettings ||
        [];

      const actionsArray = Array.isArray(settings) ? settings : (settings.actions || []);
      console.log(actionsArray.length,"length")
      if (actionsArray.length > 0) {
        return actionsArray.filter(b => b.active === true || b.active === 1);
      }

      return [
        { id: "check_in", label: "ثبت ورود پرسنل", icon: "mdi-fingerprint", color: this.value?.pallete.headColor, active: true, outlined: false },
        { id: "check_out", label: "ثبت خروج پرسنل", icon: "mdi-fingerprint", color: "error", active: true, outlined: true }
      ];
    },
    activeShiftsList() {
      if (Array.isArray(this.companyInfo.shifts)) {
        return this.companyInfo.shifts;
      }
      if (this.companyInfo.workStartTime && this.companyInfo.workEndTime) {
        return [{ name: "شیفت کاری", startTime: this.companyInfo.workStartTime, endTime: this.companyInfo.workEndTime }];
      }
      return [];
    },
    renderedFields() {
      const fieldsContainer = this.value?.AttendanceRegistrationFields;
      if (!fieldsContainer || !Array.isArray(fieldsContainer.items)) {
        return this.fallbackDefaults.map((field) => this.mapField(field));
      }
      return fieldsContainer.items
        .filter((field) => {
          const child = field.items && field.items[0];
          return child ? child.action !== false : true;
        })
        .map((field) => this.mapField(field));
    },

    pallete() { return this.value?.pallete || {}; },
    headerBackground() { return this.pallete.headColor || "linear-gradient(135deg, #1b8d39 0%, #0d5f23 100%)"; },
    bodyBackground() { return this.pallete.bodyColor || "#f8fafd"; },
    bodyTextColor() { return this.pallete.textColor || "#2c3e50"; },
    headerTextColor() { return this.pallete.headerTextColor || "#ffffff"; },
    
    primaryThemeColor() {
      const bg = this.headerBackground;
      if (!bg) return "#1b8d39";
      const hexMatch = bg.match(/#(?:[0-9a-fA-F]{3}){1,2}/);
      if (hexMatch) return hexMatch[0];
      return "#1b8d39";
    },

    previewContainerStyles() {
      return { background: this.bodyBackground, color: this.bodyTextColor };
    },
    activeToggleStyle() {
      return { backgroundColor: "#ffffff !important", color: `${this.primaryThemeColor} !important`, fontWeight: "bold" };
    },
    deactiveToggleStyle() {
      return { backgroundColor: "#ffffff !important", color: `#666565 !important` };
    },

    resolvedCompanyLogo() {
      const img = this.companyInfo.image;
      if (!img) return "";
      if (typeof img === "string") return img.startsWith("http") || img.startsWith("data:") || img.startsWith("/media") ? img : `/uploads/${img}`;
      if (img && typeof img === "object" && img.url) return img.url;
      return "";
    },

    draggableFields: {
      get() { return this.renderedFields; },
      set(newOrderedList) {
        const updatedSource = newOrderedList.map((item) => item.rawRef).filter(Boolean);
        if (!this.value.AttendanceRegistrationFields) this.$set(this.value, "AttendanceRegistrationFields", {});
        this.$set(this.value.AttendanceRegistrationFields, "items", updatedSource);
        this.$emit("input", this.value);
      },
    },
  },

  mounted() {
    this.extractQrFromUrl();
    this.updateClock();
    setInterval(this.updateClock, 1000);
    this.getUserLocation();
    this.checkDevicePlatform();
    window.addEventListener("resize", this.checkDevicePlatform);
  },

  beforeDestroy() {
    this.stopScanner();
    window.removeEventListener("resize", this.checkDevicePlatform);
  },

  methods: {
    checkDevicePlatform() {
      if (this.isAdminPreview) {
        this.isDesktopBlocked = false;
        return;
      }
      const ua = navigator.userAgent || navigator.vendor || window.opera;
      const isMobile = /Android|iPhone|iPod|Mobile|IEMobile|BlackBerry/i.test(ua);
      const isIPad = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
      this.isDesktopBlocked = !isMobile && !isIPad;
    },

    extractQrFromUrl() {
      const urlParams = new URLSearchParams(window.location.search);
      const qr = urlParams.get("qr") || urlParams.get("token") || urlParams.get("code");
      if (qr) this.urlQrCode = qr;
    },

    updateClock() {
      const now = new Date();
      this.nowTimeString = now.toLocaleTimeString("fa-IR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    },

    getUserLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            this.userLocation = { lat: pos.coords.latitude, lng: pos.coords.longitude };
            this.checkGeoFence();
          },
          (err) => { if (this.$toast) this.$toast.warning("خطای GPS:", err); },
          { enableHighAccuracy: true }
        );
      }
    },

    getDistanceFromLatLonInMeters(lat1, lon1, lat2, lon2) {
      const R = 6371e3;
      const dLat = (lat2 - lat1) * (Math.PI / 180);
      const dLon = (lon2 - lon1) * (Math.PI / 180);
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return Math.round(R * c);
    },

    checkGeoFence() {
      let targetLat = this.companyInfo.latitude || this.companyInfo.lat;
      let targetLng = this.companyInfo.longitude || this.companyInfo.lng;
      if (!targetLat && Array.isArray(this.companyInfo.coordinates) && this.companyInfo.coordinates.length === 2) {
        targetLng = this.companyInfo.coordinates[0];
        targetLat = this.companyInfo.coordinates[1];
      }
      const allowedRadius = Number(this.companyInfo.allowedRadius) || 100;
      if (this.userLocation && targetLat && targetLng) {
        this.currentDistance = this.getDistanceFromLatLonInMeters(
          this.userLocation.lat, this.userLocation.lng, targetLat, targetLng
        );
        this.isWithinRadius = this.currentDistance <= allowedRadius;
      }
    },

    resolvePersonnelMobile() {
      for (const field of this.renderedFields) {
        const title = (field.title || "").toLowerCase();
        if (title.includes("موبایل") || title.includes("همراه") || title.includes("تلفن")) {
          const val = this.previewFormModels[field.id];
          if (val) return String(val).trim();
        }
      }
      return this.value?.personnelMobile || this.companyInfo?.defaultMobile || null;
    },

    resolvePersonnelNationalCode() {
      for (const field of this.renderedFields) {
        const title = (field.title || "").toLowerCase();
        if (title.includes("ملی")) {
          const val = this.previewFormModels[field.id];
          if (val) return String(val).trim();
        }
      }
      return this.value?.nationalCode || null;
    },

    goToRegistration() {
      this.unidentifiedDialog = false;
      this.previewMode = 'form';
    },

    // هندلر دکمه‌های تردد
    handleAttendanceAction(actionLabel, actionId) {
      // توجه: دکمه دیگر به خاطر مکانی قفل نمی‌شود.
      this.pendingAction = actionLabel;
      this.pendingActionId = actionId;

      if (this.hasQrFromUrl) {
        this.submitAttendanceToBackend(this.urlQrCode, actionLabel);
      } else {
        this.scannerDialog = true;
        this.$nextTick(() => { this.startScanner(); });
      }
    },

    startScanner() {
      const videoElem = this.$refs.cameraVideo;
      if (!videoElem) return;

      this.qrScannerInstance = new QrScanner(
        videoElem,
        (result) => {
          const scannedText = result.data || result;
          this.closeScanner();
          this.submitAttendanceToBackend(scannedText, this.pendingAction);
        },
        { highlightScanRegion: false, highlightCodeOutline: false }
      );

      this.qrScannerInstance.start().catch((err) => {
        console.error("خطای فعال‌سازی دوربین:", err);
        this.closeScanner();
        this.popup.title = "خطای دوربین";
        this.popup.message = "امکان دسترسی به دوربین وجود ندارد. دسترسی مرورگر را بررسی نمایید.";
        this.popup.type = "error";
        this.popup.show = true;
      });
    },

    closeScanner() {
      this.stopScanner();
      this.scannerDialog = false;
    },

    stopScanner() {
      if (this.qrScannerInstance) {
        this.qrScannerInstance.stop();
        this.qrScannerInstance.destroy();
        this.qrScannerInstance = null;
      }
    },

    async submitAttendanceToBackend(qrCodeValue, actionName) {
      const mobile = this.resolvePersonnelMobile();
      const nationalCode = this.resolvePersonnelNationalCode();
      const currentShift = this.activeShiftsList[this.selectedShiftIndex] || null;

      if (this.selectedAuthMethod === "passkey" && !mobile) {
         this.unidentifiedDialog = true; // نمایش دیالوگ ثبت‌نام
         return;
      }

      this.isSubmitting = true;

      try {
        const response = await submitAttendanceRecord(this.$axios || window.axios, {
          qrCode: qrCodeValue,
          action: this.pendingActionId,
          lat: this.userLocation?.lat || null,
          lng: this.userLocation?.lng || null,
          shiftId: currentShift?.id || null,
          mobile: mobile,
          nationalCode: nationalCode,
          image: null,
          verificationMethod: this.selectedAuthMethod,
          vapidPublicKey: this.vapidPublicKey,
        });

        const serverTime = response?.data?.data?.time || this.nowTimeString;
        const shiftLabel = currentShift?.name ? ` در «${currentShift.name}»` : "";
        
        let warningNote = !this.isWithinRadius ? " (ثبت با خطای مکانی)" : "";

        this.popup.title = `ثبت موفق ${actionName}`;
        this.popup.message = `${actionName} شما${shiftLabel} ثبت شد.${warningNote}`;
        this.popup.time = serverTime;
        this.popup.type = "success";
        this.popup.show = true;

      } catch (err) {
        console.error("خطا در ثبت تردد:", err);
        
        // شبیه‌سازی برای دمو: اگر خطای ناشناس برگشت خورد:
        if (err.response?.status === 404 || (err.message && err.message.includes('not found'))) {
           this.unidentifiedDialog = true;
        } else {
           this.popup.title = "خطا در ثبت تردد";
           this.popup.message = err.response?.data?.message || err.message || "خطا در ارتباط با سرور رخ داد.";
           this.popup.type = "error";
           this.popup.show = true;
        }
      } finally {
        this.isSubmitting = false;
      }
    },

    nowDateTimeString() {
      const d = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },

    mapField(field) {
      const child = (field.items && field.items[0]) || {};
      const fieldType = child.type || "text";
      const title = field.title || field.label || child.title || "فیلد ثبت‌نام";
      
      // اگر false باشد یعنی اجباری است
      const isRequired = child.optional === false; 

      return {
        id: field.id || Math.random(),
        rawRef: field,
        title: title,
        type: fieldType,
        required: isRequired,
        options: child.selectItems || [],
        placeholder: this.getPlaceholderForField(title, fieldType, child.placeholder),
        icon: this.resolveFieldIcon(field.action, title, fieldType),
      };
    },

    resolveFieldIcon(configuredIcon, title = "", type = "text") {
      if (configuredIcon && typeof configuredIcon === 'string' && configuredIcon !== "mdi-form-textbox") {
        return configuredIcon;
      }
      const t = title.toLowerCase();
      if (t.includes("پرسنل") || t.includes("کارت")) return "mdi-badge-account-horizontal";
      if (t.includes("نام خانوادگی")) return "mdi-account-tie";
      if (t.includes("نام")) return "mdi-account";
      if (t.includes("موبایل") || t.includes("همراه") || t.includes("تلفن")) return "mdi-cellphone";
      if (t.includes("ملی")) return "mdi-card-account-details-outline";
      if (t.includes("واحد") || t.includes("دپارتمان") || t.includes("بخش")) return "mdi-domain";
      if (t.includes("سمت") || t.includes("شغل")) return "mdi-briefcase-outline";

      switch (type) {
        case "number": return "mdi-numeric";
        case "select": return "mdi-form-dropdown";
        case "textarea": return "mdi-text-box-outline";
        case "date": return "mdi-calendar-month-outline";
        case "time": return "mdi-clock-outline";
        default: return "mdi-form-textbox";
      }
    },

    getPlaceholderForField(title = "", type = "text", originalPlaceholder) {
      if (originalPlaceholder) return originalPlaceholder;
      const t = title.toLowerCase();
      if (t.includes("پرسنل")) return "مثال: 1042";
      if (t.includes("موبایل")) return "09xxxxxxxxx";
      if (t.includes("ملی")) return "۱۰ رقم بدون خط تیره";
      if (type === "select") return "یک مورد را انتخاب فرمایید";
      return "اینجا بنویسید...";
    },

    testPopup(type, actionName = "عملیات") {
      const now = new Date();
      this.popup.time = now.toLocaleTimeString("fa-IR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

      if (type === "success") {
        this.popup.type = 'success';
        this.popup.title = `ثبت موفق ${actionName}`;
        this.popup.message = `${actionName} شما با موفقیت ثبت شد.`;
        this.popup.show = true;
      } else if (type === "warning") {
        // شبیه‌سازی پرسنل ناشناس
        this.unidentifiedDialog = true;
      } else {
        this.popup.type = 'error';
        this.popup.title = "خطای ارتباط";
        this.popup.message = `برقراری ارتباط با پایگاه داده ناموفق بود.`;
        this.popup.show = true;
      }
    },
  },
};
</script>

<style scoped>
.attendance-preview-wrapper {
  min-height: 100%;
  direction: rtl;
  display: flex;
  flex-direction: column;
  transition: background 0.3s ease;
  position: relative;
  overflow: hidden;
}

.company-header {
  padding: 38px 14px 16px;
  border-bottom-left-radius: 24px;
  border-bottom-right-radius: 24px;
  position: relative;
  overflow: hidden;
  transition: background 0.3s ease;
}

.header-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: radial-gradient(circle at top right, rgba(255, 255, 255, 0.22), transparent 70%);
  pointer-events: none;
}

.header-content {
  position: relative;
  z-index: 2;
}

.company-avatar { border: 2px solid rgba(255, 255, 255, 0.9); }
.company-title { font-size: 15px; letter-spacing: -0.2px; }
.company-sub { font-size: 11px; }

.preview-toggle {
  background-color: rgba(255, 255, 255, 0.25) !important;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
}
.preview-toggle .v-btn {
  color: #ffffff !important;
  border: none !important;
  font-size: 11px !important;
}

.preview-body {
  flex-grow: 1;
  overflow-y: auto;
  max-height: 480px;
}
.preview-body::-webkit-scrollbar { width: 3px; }
.preview-body::-webkit-scrollbar-thumb { background: #cfd8dc; border-radius: 4px; }

.field-card { background-color: #ffffff !important; border: 1px solid #e2e8f0; transition: all 0.2s ease; }
.field-input >>> .v-input__control .v-input__slot {
  min-height: 34px !important; background-color: #f8fafc !important; border-color: #e2e8f0 !important; padding: 0 8px !important;
}
.field-input >>> input { font-size: 11px !important; }
.field-input >>> .v-select__selections { font-size: 11px !important; }

.shift-row { background-color: #f8fafc; border: 1px solid #edf2f7; cursor: pointer; transition: all 0.2s ease; }
.shift-row:hover { background-color: #f1f5f9; }
.shift-row-selected { background-color: #e8f5e9 !important; border-color: #a5d6a7 !important; }
.dir-ltr { direction: ltr; }

.main-action-btn { transition: all 0.3s ease; }
.border-light { border: 1px solid #edf2f7; }
.border-subtle { border: 1px dashed #cbd5e1; }
.bg-light { background-color: #f8fafc; }

.ghost-field { opacity: 0.4; background: #e8f5e9 !important; border: 2px dashed #4caf50 !important; }

.test-actions-bar { border-top: 1px dashed #dcdcdc; }

.qr-floating-btn {
  position: absolute; top: 14px; left: 14px; z-index: 20; background-color: rgba(255, 255, 255, 0.9) !important;
}

/* اسکنر مدرن داخل کادر گوشی */
.in-mockup-scanner-overlay {
  position: absolute; top: 0; left: 0; right: 0; bottom: 0;
  background: #0f172a; z-index: 50; display: flex; flex-direction: column;
  justify-content: space-between; border-radius: 15px;
}
.scanner-top-bar { background: rgba(15, 23, 42, 0.85); backdrop-filter: blur(8px); z-index: 2; border-bottom: 1px solid rgba(255, 255, 255, 0.1); }
.close-scanner-btn { background: rgba(255, 255, 255, 0.15) !important; }
.scanner-bottom-bar { background: rgba(15, 23, 42, 0.85); backdrop-filter: blur(8px); z-index: 2; border-top: 1px solid rgba(255, 255, 255, 0.1); }
.scanner-viewfinder-container { flex-grow: 1; position: relative; display: flex; align-items: center; justify-content: center; overflow: hidden; background: #000; }
.scanner-video-element { width: 100%; height: 100%; object-fit: cover; }
.scanner-target-box { position: absolute; width: 210px; height: 210px; box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.55); border-radius: 16px; pointer-events: none; }
.corner-border { position: absolute; width: 24px; height: 24px; border-color: #fbbf24; border-style: solid; }
.top-left { top: 0; left: 0; border-width: 4px 0 0 4px; border-top-left-radius: 12px; }
.top-right { top: 0; right: 0; border-width: 4px 4px 0 0; border-top-right-radius: 12px; }
.bottom-left { bottom: 0; left: 0; border-width: 0 0 4px 4px; border-bottom-left-radius: 12px; }
.bottom-right { bottom: 0; right: 0; border-width: 0 4px 4px 0; border-bottom-right-radius: 12px; }
.laser-beam { position: absolute; left: 5%; right: 5%; height: 2px; background: linear-gradient(90deg, transparent, #ef4444, #ff7878, #ef4444, transparent); box-shadow: 0 0 10px 2px rgba(239, 68, 68, 0.8); animation: laserSweep 2.2s infinite ease-in-out; }
@keyframes laserSweep { 0% { top: 5%; opacity: 0.2; } 50% { top: 92%; opacity: 1; } 100% { top: 5%; opacity: 0.2; } }

.scanner-fade-enter-active, .scanner-fade-leave-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.scanner-fade-enter, .scanner-fade-leave-to { opacity: 0; transform: scale(0.96); }
</style>
